'use client';

import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { DashboardStatCard } from './dashboard-stat-card';
import type { StatCardData } from '../types';

interface DashboardSectionProps {
  title: string;
  description?: string;
  cards: StatCardData[];
  isLoading?: boolean;
  skeletonCount?: number;
  columnsClassName?: string;
}

export function DashboardSection({
  title,
  description,
  cards,
  isLoading = false,
  skeletonCount,
  columnsClassName = 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
}: DashboardSectionProps) {
  if (!isLoading && cards.length === 0) {
    return null;
  }

  const skeletonItems = skeletonCount ?? Math.max(cards.length, 4);

  return (
    <section className='space-y-3'>
      <header>
        <h2 className='text-lg font-semibold tracking-tight'>{title}</h2>
        {description ? (
          <p className='text-muted-foreground text-sm'>{description}</p>
        ) : null}
      </header>
      <div className={cn('grid gap-4', columnsClassName)}>
        {isLoading
          ? Array.from({ length: skeletonItems }).map((_, i) => (
              <Skeleton key={i} className='h-24 w-full rounded-lg' />
            ))
          : cards.map((card) => (
              <DashboardStatCard key={card.label} data={card} />
            ))}
      </div>
    </section>
  );
}
